import { gql, useMutation, useQuery } from '@apollo/client';
import React, { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useDispatch } from 'react-redux';
import AddIcon from 'mdi-react/AddIcon';
import { toast } from 'react-toastify';
import styled from '../../../../style/styled';
import { setModalType } from '../../../../store/modal/actions';
import { ModalType } from '../../../../store/modal/types';
import { EXPLORE_WEB_GetTourPlansForAddToPlanModal as TourPlansData } from '../__generated__/EXPLORE_WEB_GetTourPlansForAddToPlanModal';
import {
  EXPLORE_WEB_AddItemToTourPlan as AddingData,
  EXPLORE_WEB_AddItemToTourPlanVariables as AddingVariables,
} from '../__generated__/EXPLORE_WEB_AddItemToTourPlan';
import { COLORS } from '../../../../style/colors';
import { UNKNOWN_ERROR } from '../../../../consts';
import { captureInSentry } from '../../../config/reporting/captureInSentry';
import { AddIconContainer } from './styledComponents/AddIconContainer';

const GET_TOUR_PLANS_QUERY = gql`
  query EXPLORE_WEB_GetTourPlansForAddToPlanModal {
    result: getTourPlans(input: {}) {
      id
      name
    }
  }
`;

const ADD_ITEM_TO_TOUR_PLAN = gql`
  mutation EXPLORE_WEB_AddItemToTourPlan($input: AddItemToTourPlanInput!) {
    result: addItemToTourPlan(input: $input) {
      tourPlan {
        id
      }
      error {
        id
        message
        type
      }
    }
  }
`;

let _variables: AddingVariables | null = null;

const getVariables = () => _variables;

const setVariables = (variables: AddingVariables) => {
  _variables = variables;
};

interface Props {
  tourID?: string;
  placeID?: string;
}

export const AddItemsToExistingTourPlan: React.FC<Props> = ({
  placeID,
  tourID,
}) => {
  const dispatch = useDispatch();

  const { data } = useQuery<TourPlansData, null>(GET_TOUR_PLANS_QUERY, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [add, { loading: adding }] = useMutation<AddingData, AddingVariables>(
    ADD_ITEM_TO_TOUR_PLAN,
    {
      onCompleted: ({ result }) => {
        if (result.error) {
          const errorMessage = result.error?.message || UNKNOWN_ERROR;

          captureInSentry(errorMessage, { variables: getVariables() });

          toast.error(errorMessage);
        } else {
          onSuccess();
        }
      },
      onError: (error) => {
        Sentry.withScope((scope) => {
          scope.setExtra('variables', getVariables());
          Sentry.captureException(error);
        });

        toast.error(UNKNOWN_ERROR);
      },
    }
  );

  const options = (data?.result || []).map((plan) => ({
    label: plan.name,
    value: plan.id,
  }));

  const onSuccess = useCallback(() => {
    dispatch(setModalType({ modalType: ModalType.NONE, modalData: null }));
  }, [dispatch]);

  return (
    <>
      {options.map((option) => (
        <ExistingPlan key={option.value}>
          <p>{option.label}</p>
          <AddIconContainer>
            <AddIcon
              onClick={() => {
                if (adding) {
                  return;
                }

                const variables: AddingVariables = {
                  input: { tourPlanID: option.value },
                };

                if (tourID) {
                  variables.input.tourID = tourID;
                }

                if (placeID) {
                  variables.input.placeID = placeID;
                }

                setVariables(variables);

                add({ variables });
              }}
            />
          </AddIconContainer>
        </ExistingPlan>
      ))}
    </>
  );
};

const ExistingPlan = styled.div`
  padding: 8px;
  background: ${COLORS.offwhite};
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (hover: none) and (pointer: coarse) {
    padding: 15px;
  }
`;
